<template>
  <div v-if="selectedFiltersCount > 0" class="product-filter product-filter__selected-facets">
    <h3 class="sr-only">
      {{ $t("currentFilter") }}
    </h3>
    <div
      v-for="pill in pills"
      :key="pill.facetValueId"
      class="tag tag--small"
      role="button"
      :tabindex="productFilterStore.isLoading ? -1 : 0"
      @click="!productFilterStore.isLoading && removeFilterOption(pill)"
      @keyup.enter="removeFilterOption(pill)"
      :aria-disabled="productFilterStore.isLoading"
      :class="{ 'tag--disabled': productFilterStore.isLoading }"
    >
      <span class="tag__text">{{ formatFilterSize(pill.label) }}</span>
      <Icon :type="IconTypes.Close" :size="IconSizes.Small" />
    </div>

    <!-- Clear All Button -->
    <span v-if="selectedFiltersCount > 1" class="product-filter__clear-all u-hide-for-medium">
      <button
        class="btn--small btn--link"
        type="button"
        :disabled="productFilterStore.isLoading"
        @click="clearAllFilters"
      >
        {{ $t("clearAll") }}
      </button>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import Icon from "../../components/icon/icon.vue";
import { IconSizes, IconTypes } from "../../components/icon/icon.model";
import { Facet, FacetValue, Pill } from "../../services/search.service";
import { getSelectedValuesFromFacet } from "../../utils/search.utils";
import { useProductFilterStore } from "../../stores/productFilterStore";
import { formatFilterSize } from "../../mixins/productSize.mixin"

const props = defineProps<{
  facets: Array<Facet>;
}>();

const productFilterStore = useProductFilterStore();
const selectedFiltersCount = computed(() => productFilterStore.selectedFiltersCount);

const emit = defineEmits<{
  (
    e: "upsertFacet",
    facet: { facet: Facet; selectedValues: Array<string>; useFilter: boolean },
  );
}>();

const pills = computed((): Pill[] => {
  const pills = props.facets.map((facet) => {
    return getSelectedValuesFromFacet(facet).map((facetValue: FacetValue) => {
      return {
        facetId: facet.id,
        facetValueId: facetValue.id,
        label: facetValue.label,
      };
    });
  });

  return ([] as Pill[]).concat.apply([], pills);
});

/**
 * Click
 */
function removeFilterOption({ facetId, facetValueId }) {
  const facet = props.facets.find((facet) => facet.id === facetId);

  const selectedValues = getSelectedValuesFromFacet(facet as Facet)
    .filter((facetValue: FacetValue) => {
      return facetValue.id !== facetValueId;
    })
    .map((facetValue) => facetValue.id);

    const updatedFacet = {
    facet: props.facets.find((facet) => facet.id === facetId) as Facet,
    selectedValues,
    useFilter: true,
  };

  emit("upsertFacet", updatedFacet);
}

function clearAllFilters() {
  // Loop over all facets and emit an "empty" selectedValues array
  props.facets.forEach((facet) => {
    emit("upsertFacet", {
      facet,
      selectedValues: [],
      useFilter: true,
    });
  });
}

</script>
